import { ReactNode } from 'react';
import { Router, useRouter, type BaseLocationHook, type BaseSearchHook } from 'wouter';
import { navigate as browserNavigate } from 'wouter/use-browser-location';

export type StaticRouterNavigate = (
    to: string,
    options?: { replace?: boolean; state?: unknown },
) => void;

interface StaticRouterProps {
    location: string;
    navigate?: StaticRouterNavigate | null;
    children: ReactNode;
}

// Router pinned to a location: routing hooks inside resolve to `location`, not to the
// browser's. Navigation goes to `navigate` when given, otherwise to the browser history.
function StaticRouter({ location, navigate = null, children }: StaticRouterProps) {
    const { parser } = useRouter();
    const searchIndex = location.indexOf('?');
    const pathname = searchIndex !== -1 ? location.substring(0, searchIndex) : location;
    const search = searchIndex !== -1 ? location.substring(searchIndex + 1) : '';
    const finalNavigate = navigate !== null ? navigate : browserNavigate;

    // Kept stable (memoized by the React Compiler) so the router context only changes when
    // the location or the navigation does.
    const hook: BaseLocationHook = () => [pathname, finalNavigate];
    const searchHook: BaseSearchHook = () => search;

    return (
        <Router parser={parser} hook={hook} searchHook={searchHook}>
            {children}
        </Router>
    );
}

export default StaticRouter;
